import React, { useState, useEffect, useCallback, useContext } from 'react';
import { getDepositMethodsApi } from '../../utils/api/Lead/depositMethods/getDepositMethodsApi';
import { createDepositMethodApi } from '../../utils/api/Lead/depositMethods/createDepositMethodApi';
import { deleteDepositMethodApi } from '../../utils/api/Lead/depositMethods/deleteDepositMethodApi';
import { updateDepositMethodApi } from '../../utils/api/Lead/depositMethods/updateDepositMethodApi';
import { DepositMethodModal } from './components/DepositMethodModal';
import { ConfirmationModal } from '../../components/UI/Custom/ConfirmationModal';

import { getMyAccountApi } from '../../utils/api/Account/getMyAccountApi';
import { PermissionDenied } from '../../components/UI/Custom/PermissionDenied';

import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import { Notification } from '../../components/UI/Notifications';
import { LoadingCircle } from '../../components/UI/Custom/LoadingCircle';
import { DropdownSelector } from '../../components/UI/DropdownSelector';

import styles from "../../styles/pages/DepositMethods/DepositMethodsPage.module.css"
import { LangContext } from '../../context/LangContext';
import { ThemeContext } from '../../context/ThemeContext';
import { handleError } from '../../utils/custom/errorHandler';

const translations = {
    ru: {
        title: 'Управление Методами Депозита',
        addMethod: 'Добавить новый метод',
        id: 'ID',
        key: 'Ключ',
        type: 'Тип',
        label: 'Название (Label)',
        address: 'Адрес',
        contentHtml: 'Контент (HTML)',
        icon: 'Иконка',
        sort: 'Сортировка',
        active: 'Активность',
        hidden: 'Скрыт',
        actions: 'Действия',
        activeStatus: 'Активен',
        inactiveStatus: 'Неактивен',
        hiddenStatus: 'Скрыт',
        visibleStatus: 'Виден',
        noMethods: 'Методы депозита не найдены.',
        savingError: 'Ошибка при сохранении метода депозита.',
        createSuccess: (label) => `Метод депозита "${label}" успешно создан.`,
        updateSuccess: (label) => `Метод депозита "${label}" успешно обновлен.`,
        loadingError: 'Произошла ошибка при загрузке данных.',
        deleteSuccess: (label) => `Метод депозита "${label}" успешно удален.`,
        page: 'Пред.',
        next: 'След.',
        perPage: (count) => `${count} методов`,
        confirmTitle: 'Подтвердите удаление метода',
        confirmMessage: (label, id) => `Вы уверены, что хотите удалить метод депозита "${label}" (ID: ${id})? Это действие нельзя отменить.`,
        deleteConfirm: 'Удалить',
        deleting: 'Удаление...',
        edit: 'Изменить',
        delete: 'Удалить',
        accessDenied: 'Доступ на просмотр страницы управления методами депозита отсутствует',
        cancelText: 'Отмена',
    },
    en: {
        title: 'Deposit Methods Management',
        addMethod: 'Add New Method',
        id: 'ID',
        key: 'Key',
        type: 'Type',
        label: 'Label',
        address: 'Address',
        contentHtml: 'Content (HTML)',
        icon: 'Icon',
        sort: 'Sort',
        active: 'Active',
        hidden: 'Hidden',
        actions: 'Actions',
        activeStatus: 'Active',
        inactiveStatus: 'Inactive',
        hiddenStatus: 'Hidden',
        visibleStatus: 'Visible',
        noMethods: 'No deposit methods found.',
        savingError: 'Error saving deposit method.',
        createSuccess: (label) => `Deposit method "${label}" successfully created.`,
        updateSuccess: (label) => `Deposit method "${label}" successfully updated.`,
        loadingError: 'An error occurred while loading data.',
        deleteSuccess: (label) => `Deposit method "${label}" successfully deleted.`,
        page: 'Prev.',
        next: 'Next',
        perPage: (count) => `${count} methods`,
        confirmTitle: 'Confirm Deletion',
        confirmMessage: (label, id) => `Are you sure you want to delete the deposit method "${label}" (ID: ${id})? This action cannot be undone.`,
        deleteConfirm: 'Delete',
        deleting: 'Deleting...',
        edit: 'Edit',
        delete: 'Delete',
        accessDenied: 'Access to view the deposit methods management page is denied',
        cancelText: 'Cancel',
    },
};

const ITEMS_PER_PAGE_KEY = 'depositMethodsItemsPerPage';

export const DepositMethodsPage = () => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const t = translations[langSelected] || translations.ru;

    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(() => {
        const storedValue = localStorage.getItem(ITEMS_PER_PAGE_KEY);
        return storedValue ? parseInt(storedValue, 10) : 15;
    });
    const [total, setTotal] = useState(0);
    const [searchTerm] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [editingMethod, setEditingMethod] = useState(null);

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [methodToDelete, setMethodToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [permissions, setPermissions] = useState({});
    const [isPermissionsLoaded, setIsPermissionsLoaded] = useState(false);


    const totalPages = Math.ceil(total / perPage);

    const leadsPerPageOptions = [
        { label: t.perPage(15), value: 15 },
        { label: t.perPage(30), value: 30 },
        { label: t.perPage(50), value: 50 },
    ];

    useEffect(() => {
        const fetchPermissions = async () => {
            try {
                const accountData = await getMyAccountApi();

                if (accountData && accountData.permissions) {
                    const permissionsObject = {};
                    accountData.permissions.forEach(permission => {
                        permissionsObject[permission.name] = permission;
                    });
                    setPermissions(permissionsObject);
                }
            } catch (error) {
                handleError(error.response?.data || error, setError);
            } finally {
                setIsPermissionsLoaded(true);
            }
        };

        fetchPermissions();
    }, []);

    const fetchMethods = useCallback(async (currentPage, currentPerPage, currentSearchTerm) => {
        if (!isPermissionsLoaded || !permissions['lead.list_deposit_methods']) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const data = await getDepositMethodsApi(currentPage, currentPerPage, currentSearchTerm);

            setMethods(data.items || []);
            setTotal(data.total || 0);
            setPage(data.page || currentPage);
            setPerPage(data.per_page || currentPerPage);
        } catch (err) {
            setError(err.message || t.loadingError);
            setMethods([]);
        } finally {
            setLoading(false);
        }
    }, [t, isPermissionsLoaded, permissions]);

    useEffect(() => {
        if (isPermissionsLoaded) {
            const timer = setTimeout(() => {
                fetchMethods(page, perPage, searchTerm);
            }, 300);

            return () => clearTimeout(timer);
        }
    }, [fetchMethods, page, perPage, searchTerm, isPermissionsLoaded]);

    const handlePreviousPage = () => {
        setPage(prevPage => Math.max(prevPage - 1, 1));
    };

    const handleNextPage = () => {
        setPage(prevPage => Math.min(prevPage + 1, totalPages));
    };

    const handlePageClick = (pageNumber) => {
        setPage(pageNumber);
    };

    const handleItemsPerPageChange = (name, value) => {
        const newValue = parseInt(value, 10);
        if (!isNaN(newValue)) {
            setPerPage(newValue);
            localStorage.setItem(ITEMS_PER_PAGE_KEY, newValue.toString());
            setPage(1);
        }
    };

    const renderActiveStatus = (isActive) => {
        return isActive
            ? <span className={`${styles.statusActive} ${isDark ? styles.darkStatusActive : ''}`}><CheckCircleOutlineIcon fontSize="small" /> {t.activeStatus}</span>
            : <span className={`${styles.statusInactive} ${isDark ? styles.darkStatusInactive : ''}`}><CancelOutlinedIcon fontSize="small" /> {t.inactiveStatus}</span>;
    }

    const renderHiddenStatus = (isHidden) => {
        return isHidden
            ? <span className={`${styles.statusInactive} ${isDark ? styles.darkStatusInactive : ''}`}><VisibilityOffOutlinedIcon fontSize="small" /> {t.hiddenStatus}</span>
            : <span className={`${styles.statusActive} ${isDark ? styles.darkStatusActive : ''}`}><CheckCircleOutlineIcon fontSize="small" /> {t.visibleStatus}</span>;
    };

    const handleOpenCreateModal = () => {
        if (!permissions['lead.create_deposit_methods']) {
            setError('У вас нет прав для создания метода депозита.');
            return;
        }
        setError(null);
        setSuccessMessage(null);
        setEditingMethod(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingMethod(null);
    };

    const handleDeleteFromEditModal = () => {
        if (!editingMethod) return;

        handleCloseModal();
        handleDeleteClick(editingMethod);
    };

    const handleSaveDepositMethod = async (formData, id) => {
        setIsSaving(true);
        setError(null);
        setSuccessMessage(null);

        if (id && !permissions['lead.update_deposit_methods']) {
            setError('У вас нет прав для обновления метода депозита.');
            setIsSaving(false);
            return;
        } else if (!id && !permissions['lead.create_deposit_methods']) {
            setError('У вас нет прав для создания метода депозита.');
            setIsSaving(false);
            return;
        }

        try {
            const dataToSend = {
                ...formData,
                sort: Number(formData.sort),
                is_active: Boolean(formData.is_active),
                is_hidden: Boolean(formData.is_hidden),
            };

            let response;

            if (id) {
                const { key, type, id: _, ...dataForUpdate } = dataToSend;

                response = await updateDepositMethodApi(id, dataForUpdate);
                setSuccessMessage(t.updateSuccess(response.label));

            } else {
                const { id: _, ...dataForCreation } = dataToSend;
                response = await createDepositMethodApi(dataForCreation);
                setSuccessMessage(t.createSuccess(response.label));
            }

            setIsModalOpen(false);
            fetchMethods(page, perPage, searchTerm);

        } catch (err) {
            setError(err.message || t.savingError);
        } finally {
            setIsSaving(false);
        }
    };

    const handleEdit = (method) => {
        if (!permissions['lead.update_deposit_methods']) {
            setError('У вас нет прав для редактирования метода депозита.');
            return;
        }
        setError(null);
        setSuccessMessage(null);
        setEditingMethod(method);
        setIsModalOpen(true);
    };

    const handleDeleteClick = (method) => {
        if (!permissions['lead.delete_deposit_methods']) {
            setError('У вас нет прав для удаления метода депозита.');
            return;
        }
        setError(null);
        setMethodToDelete(method);
        setIsConfirmModalOpen(true);
    };

    const handleCloseConfirmModal = () => {
        setIsConfirmModalOpen(false);
        setMethodToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!methodToDelete || !permissions['lead.delete_deposit_methods']) return;

        setIsDeleting(true);
        setError(null);
        setSuccessMessage(null);

        try {

            await deleteDepositMethodApi(methodToDelete.id);

            setSuccessMessage(t.deleteSuccess(methodToDelete.label));

            fetchMethods(page, perPage, searchTerm);

        } catch (err) {
            setError(err.message || t.loadingError);
        } finally {
            setIsDeleting(false);
            handleCloseConfirmModal();
        }
    };

    const tableHeaders = [
        { label: t.id, key: 'id' },
        { label: t.key, key: 'key' },
        { label: t.type, key: 'type' },
        { label: t.label, key: 'label' },
        { label: t.address, key: 'address' },
        { label: t.contentHtml, key: 'content_html' },
        { label: t.icon, key: 'icon' },
        { label: t.sort, key: 'sort' },
        { label: t.active, key: 'is_active' },
        { label: t.hidden, key: 'is_hidden' },
        { label: t.actions, key: 'actions', sortable: false },
    ];

    const renderPaginationButtons = () => {
        const pages = [];
        const maxPageButtons = 5;
        let startPage = Math.max(1, page - Math.floor(maxPageButtons / 2));
        let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

        if (endPage - startPage + 1 < maxPageButtons) {
            startPage = Math.max(1, endPage - maxPageButtons + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    className={`${styles.paginationButton} ${i === page ? styles.activePage : ''} ${isDark ? styles.darkPaginationButton : ''} ${i === page && isDark ? styles.darkActivePage : ''}`}
                    onClick={() => handlePageClick(i)}
                    disabled={loading}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    if (!isPermissionsLoaded) {
        return (
            <div className={`${styles.loadingBlock} ${isDark ? styles.darkLoadingBlock : ''}`}>
                <LoadingCircle size={40} thickness={3} isDark={isDark} />
            </div>
        );
    }

    const canList = permissions['lead.list_deposit_methods'];
    const canCreate = permissions['lead.create_deposit_methods'];
    const canUpdate = permissions['lead.update_deposit_methods'];
    const canDelete = permissions['lead.delete_deposit_methods'];


    if (!canList) {
        return (
            <PermissionDenied
                text={t.accessDenied}
            />
        );
    }

    return (
        <div className={`${styles.container} ${isDark ? styles.darkContainer : ''}`}>
            <h2 className={`${isDark ? styles.darkHeading : ''}`}><PaidOutlinedIcon fontSize="large" className={styles.headerIcon} />{t.title}</h2>

            {successMessage && <Notification message={successMessage} type="success" />}
            {error && <Notification message={error} type="error" />}

            <div className={`${styles.content} ${isDark ? styles.darkContent : ''}`}>
                <div className={styles.topBar}>
                    <div className={styles.itemsPerPageSelector}>
                        <DropdownSelector
                            filterName="perPage"
                            filterKey="perPage"
                            selectedValue={perPage}
                            handleFilterChange={handleItemsPerPageChange}
                            options={leadsPerPageOptions}
                            hasError={false}
                            errorMessage=""
                            isDark={isDark}
                        />
                    </div>

                    <div className={styles.action}>
                        {canCreate && (
                            <div className={styles.addButtonContainer}>
                                <button
                                    className={`${styles.addButton} ${isDark ? styles.darkAddButton : ''}`}
                                    onClick={handleOpenCreateModal}
                                    disabled={loading}
                                >
                                    <AddIcon fontSize="small" />
                                    <span className={styles.addButtonText}>{t.addMethod}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className={`${styles.loadingBlock} ${isDark ? styles.darkLoadingBlock : ''}`}>
                        <LoadingCircle size={30} thickness={1} isDark={isDark} />
                    </div>
                ) : (
                    <>
                        <div className={styles.tableWrapper}>
                            <table className={styles.table}>
                                <thead className={`${styles.tableHeader} ${isDark ? styles.darkTableHeader : ''}`}>
                                    <tr>
                                        {tableHeaders.map((header) => (
                                            <th
                                                key={header.key}
                                                className={`${styles.tableHeaderTh} ${isDark ? styles.darkTableHeaderTh : ''}`}
                                                style={{ cursor: 'default' }}
                                            >
                                                <div className={styles.sortableElement}>
                                                    {header.label}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {methods.length > 0 ? (
                                        methods.map((method) => (
                                            <tr key={method.id} className={`${styles.tableRow} ${isDark ? styles.darkTableRow : ''}`}>
                                                <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>{method.id}</td>
                                                <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>{method.key}</td>
                                                <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>{method.type}</td>
                                                <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>{method.label}</td>
                                                <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>{method.address}</td>

                                                <td
                                                    className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}
                                                    dangerouslySetInnerHTML={{ __html: method.content_html || '' }}
                                                />
                                                <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>
                                                    {method.icon ? (
                                                        <img src={method.icon} alt={method.label} className={styles.methodIcon} />
                                                    ) : (
                                                        <span className={styles.emptyIcon}>-</span>
                                                    )}
                                                </td>
                                                <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>{method.sort}</td>
                                                <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>{renderActiveStatus(method.is_active)}</td>
                                                <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>{renderHiddenStatus(method.is_hidden)}</td>
                                                <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>
    <div className={styles.actionButtonsContainer}>
        {canUpdate && (
            <button
                onClick={() => handleEdit(method)}
                className={`${styles.editButton} ${isDark ? styles.darkEditButton : ''}`}
            >
                <EditIcon fontSize="small" />
                {t.edit}
            </button>
        )}
        {canDelete && (
            <button
                onClick={() => handleDeleteClick(method)}
                className={`${styles.deleteButton} ${isDark ? styles.darkDeleteButton : ''}`}
            >
                <DeleteIcon fontSize="small" />
                {t.delete}
            </button>
        )}
        {!canUpdate && !canDelete && (
            <span className={`${isDark ? styles.darkNoActions : ''}`} style={{ color: '#aaa' }}>—</span>
        )}
    </div>
</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={tableHeaders.length} align="center" className={`${styles.noLeadsMessage} ${isDark ? styles.darkNoMethodsMessage : ''}`}>
                                                {t.noMethods}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {total > 0 && (
                            <div className={`${styles.paginationWrapper} ${isDark ? styles.darkPaginationWrapper : ''}`}>
                                <div className={styles.pagination}>


                                    <button
                                        className={`${styles.paginationButton} ${isDark ? styles.darkPaginationButton : ''}`}
                                        onClick={handlePreviousPage}
                                        disabled={page === 1 || loading}
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                        {t.page}
                                    </button>
                                    <div className={styles.pageButtonsWrapper}>
                                        {renderPaginationButtons()}
                                    </div>
                                    <button
                                        className={`${styles.paginationButton} ${isDark ? styles.darkPaginationButton : ''}`}
                                        onClick={handleNextPage}
                                        disabled={page === totalPages || loading}
                                    >
                                        {t.next}
                                        <ChevronRightIcon fontSize="small" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>


            <DepositMethodModal
                open={isModalOpen}
                handleClose={handleCloseModal}
                handleSave={handleSaveDepositMethod}
                handleDelete={editingMethod ? handleDeleteFromEditModal : null}
                isSubmitting={isSaving}
                isDeleting={false}
                initialData={editingMethod}
            />

            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={handleCloseConfirmModal}
                onConfirm={handleConfirmDelete}

                title={t.confirmTitle}
                message={methodToDelete ? t.confirmMessage(methodToDelete.label, methodToDelete.id) : t.confirmTitle}
                confirmText={isDeleting ? t.deleting : t.deleteConfirm}
                cancelText={t.cancelText}
            />
        </div>
    );
};
